import React from 'react';
import { useLocale } from 'hooks';
import { Box, Grid, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useFormContext } from 'react-hook-form';

import { StepContainer, IconTile } from 'ui';
import { GenericField, ScanField } from 'form/fields';
import { ErrorIcon, ThumbIcon } from 'ui/icons';
import { UploadField } from '../../form/fields/uploadField/UploadField';
import { useAssets } from '../../hooks/useAssets/useAssets';

import { useStyles } from './StepFormVerifier.styles';
import { StepFormVerifierProps } from './StepFormVerifier.types';

export const StepFormVerifier = ({ goToNextStep, goToPreviousStep, onSubmit }: StepFormVerifierProps) => {
  const { tileContainer, sendIcon, iconTileTitle, rotateIcon, container } = useStyles();
  const { formatMessage } = useLocale();
  const { setValue, watch } = useFormContext();
  const theme = useTheme();
  const { id, meterSerial } = watch(['id', 'meterSerial']);

  const { getAssetById } = useAssets();
  const asset = getAssetById(id);

  const handleSubmit = () => {
    onSubmit();
    goToNextStep();
  };

  const canBeVerified = asset?.digitalMeter === meterSerial;
  return (
    <StepContainer title={formatMessage({ id: 'verify_installation.formVerifierTitle' })}>
      <Grid container spacing={1} className={container}>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('id', value)}
            label={formatMessage({ id: 'issue_battery.form.id' })}
            name="id"
          />
        </Grid>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('meterSerial', value)}
            label={formatMessage({ id: 'verify_installation.meter_serial' })}
            name="meterSerial"
          />
        </Grid>
        {!canBeVerified && (
          <Grid item xs={12}>
            <Box display="flex" mt={2} mb={2}>
              <Box mr={3}>
                <ErrorIcon />
              </Box>
              <Typography>{formatMessage({ id: 'verify_installation.meter_not_found' })}</Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <UploadField
            label={formatMessage({ id: 'verify_installation.attachCertPhoto' })}
            name="certPhoto"
            compressImage
          />
        </Grid>
        <Grid item xs={12}>
          <GenericField label={formatMessage({ id: 'verify_installation.comment' })} name="comment" multiline />
        </Grid>
      </Grid>
      <Grid container spacing={1} className={tileContainer}>
        <Grid container item xs={6} justify="center">
          <IconTile
            backgroundColor={canBeVerified ? theme.palette.secondary.main : theme.palette.gray.dark}
            color={theme.palette.text.primary}
            title={formatMessage({ id: 'verify_installation.verify' })}
            icon={<ThumbIcon classes={{ root: sendIcon }} />}
            onClick={canBeVerified ? handleSubmit : () => null}
            titleClassName={iconTileTitle}
          />
        </Grid>
        <Grid container item xs={6} justify="center">
          <IconTile
            backgroundColor={theme.palette.red.main}
            color={theme.palette.text.primary}
            title={formatMessage({ id: 'verify_installation.reinstallation' })}
            icon={<ThumbIcon classes={{ root: sendIcon }} className={rotateIcon} />}
            onClick={goToPreviousStep}
            titleClassName={iconTileTitle}
          />
        </Grid>
      </Grid>
    </StepContainer>
  );
};
